import React, {useEffect, useRef} from 'react'
import AdyenCheckout from '@adyen/adyen-web'
import '@adyen/adyen-web/dist/adyen.css'
import {Spinner, Flex, Box} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {
    getCurrencyValueForApi,
    getGPShippingOptionParameters
} from '../../../adyenOverrides/utils/parsers.mjs'
import {AdyenPaymentsService} from '../../../adyenOverrides/services/payments'
import {AdyenShippingMethodsService} from '../../../adyenOverrides/services/shipping-methods'
import {AdyenShippingAddressService} from '../../../adyenOverrides/services/shipping-address'
import {EXPRESS_MESSAGES, PAYMENT_METHODS} from '../../../adyenOverrides/utils/constants.mjs'
import {getGpayStyles} from './index.style'
import useAdyenExpressCheckout from '../../../adyenOverrides/hooks/useAdyenExpressCheckout'

const PAYMENT_METHOD = PAYMENT_METHODS.GOOGLE_PAY
export const getGooglePaymentMethodConfig = (paymentMethodsResponse) => {
    const googlePayPaymentMethod = paymentMethodsResponse?.paymentMethods?.find(
        (pm) => pm.type === PAYMENT_METHOD
    )
    return googlePayPaymentMethod?.configuration || null
}

export const getCustomerShippingDetails = (shippingAddress, email) => {
    return {
        deliveryAddress: {
            city: shippingAddress.locality,
            country: shippingAddress.countryCode,
            houseNumberOrName: shippingAddress.address2,
            postalCode: shippingAddress.postalCode,
            stateOrProvince: shippingAddress.administrativeArea,
            street: shippingAddress.address1
        },
        profile: {
            firstName: shippingAddress.name?.split(' ')[0] || '',
            lastName: shippingAddress.name?.split(' ').slice(1).join(' ') || '',
            email: email || '',
            phone: shippingAddress.phoneNumber || ''
        }
    }
}

// 'inputAddress' is the billing address if available, else we will fall back to the shipping address
export const getCustomerBillingDetails = (inputAddress) => {
    return {
        billingAddress: {
            city: inputAddress.locality,
            country: inputAddress.countryCode,
            houseNumberOrName: inputAddress.address2,
            postalCode: inputAddress.postalCode,
            stateOrProvince: inputAddress.administrativeArea,
            street: inputAddress.address1
        }
    }
}

export const updateShippingAddress = async (
    authToken,
    site,
    basket,
    shippingAddress,
    fetchShippingMethods
) => {
    try {
        let adyenShippingMethods
        if (basket?.c_isBopisBossGCOnly || basket?.c_isStorePickup) {
            const {id, description, name, price} = basket.shipments[0].shippingMethod
            adyenShippingMethods = {
                applicableShippingMethods: [{id, description, name, price}],
                defaultShippingMethodId: id
            }
        } else {
            const adyenShippingAddressService = new AdyenShippingAddressService(authToken, site)
            const response = await adyenShippingAddressService.updateShippingAddress(
                basket.basketId,
                getCustomerShippingDetails(shippingAddress, basket?.customerInfo?.email)
            )

            if (response.error) {
                return {
                    error: {
                        reason: 'SHIPPING_ADDRESS_UNAVAILABLE',
                        message: 'Cannot ship to the selected address',
                        intent: 'SHIPPING_ADDRESS'
                    }
                }
            }

            adyenShippingMethods = await fetchShippingMethods(basket?.basketId, site, authToken)
        }

        let shippingOptionId = adyenShippingMethods.defaultShippingMethodId

        // If the default shipping method is not applicable for this address, update to the first applicable
        if (
            !adyenShippingMethods.applicableShippingMethods.some((sm) => sm.id === shippingOptionId)
        ) {
            shippingOptionId = adyenShippingMethods.applicableShippingMethods[0].id
            adyenShippingMethods.defaultShippingMethodId = shippingOptionId
        }
        return updateShippingOption(authToken, site, basket, shippingOptionId, adyenShippingMethods)
    } catch (error) {
        return {
            error: {
                reason: 'SHIPPING_ADDRESS_UNAVAILABLE',
                message: 'Cannot ship to the selected address',
                intent: 'SHIPPING_ADDRESS'
            }
        }
    }
}

export const updateShippingOption = async (
    authToken,
    site,
    basket,
    shippingOptionId,
    adyenShippingMethods = null
) => {
    try {
        const adyenShippingMethodsService = new AdyenShippingMethodsService(authToken, site)
        const response = await adyenShippingMethodsService.updateShippingMethod(
            shippingOptionId,
            basket.basketId
        )

        if (response.error) {
            return {
                error: {
                    reason: 'SHIPPING_OPTION_UNAVAILABLE',
                    message: 'Cannot ship to the selected address',
                    intent: 'SHIPPING_OPTION'
                }
            }
        }

        const paymentDataRequestUpdate = {
            newTransactionInfo: {
                countryCode: response.currency,
                currencyCode: response.currency,
                totalPriceStatus: 'FINAL',
                totalPriceLabel: 'Total',
                totalPrice: `${response.orderTotal}`
            }
        }
        // If we were called by updateShippingAddress we will have adyenShippingMethods
        // We need to update the shippingOptionParameters for applicable shipping methods to the new address
        if (adyenShippingMethods) {
            paymentDataRequestUpdate.newShippingOptionParameters = {
                ...getGPShippingOptionParameters(adyenShippingMethods, response.currency)
            }
        }
        return {
            paymentDataRequestUpdate: paymentDataRequestUpdate,
            newBasket: response
        }
    } catch (error) {
        return {
            error: {
                reason: 'SHIPPING_OPTION_UNAVAILABLE',
                message: 'Error updating shipping option',
                intent: 'SHIPPING_OPTION'
            }
        }
    }
}

export const getGoogleButtonConfig = (
    authToken,
    site,
    basket,
    adyenPaymentMethods,
    navigate,
    fetchShippingMethods
) => {
    const googlePayConfig = getGooglePaymentMethodConfig(adyenPaymentMethods)
    const buttonConfig = {
        buttonType: 'order',
        showPayButton: true,
        isExpress: true,
        billingAddressRequired: true,
        billingAddressParameters: {format: 'FULL', phoneNumberRequired: true},
        emailRequired: true,
        configuration: googlePayConfig,
        amount: {
            value: getCurrencyValueForApi(basket.orderTotal, basket.currency),
            currency: basket.currency
        },
        shippingOptionRequired: true,
        shippingAddressRequired: true,
        shippingAddressParameters: {allowedCountryCodes: ['US'], phoneNumberRequired: true},
        onAuthorized: async (data) => {
            try {
                let custShippingDetails = getCustomerShippingDetails(
                    data?.shippingAddress,
                    data?.email
                )
                if (basket?.c_isStorePickup) {
                    custShippingDetails.deliveryAddress = null
                }
                const state = {
                    data: {
                        paymentType: 'express',
                        paymentMethod: {
                            type: PAYMENT_METHOD,
                            googlePayToken: data.paymentMethodData.tokenizationData.token
                        },
                        ...custShippingDetails,
                        ...getCustomerBillingDetails(
                            data?.paymentMethodData?.info?.billingAddress || data?.shippingAddress
                        )
                    }
                }
                const adyenPaymentService = new AdyenPaymentsService(authToken, site)
                const paymentsResponse = await adyenPaymentService.submitPayment(
                    {
                        ...state.data,
                        origin: state.data.origin ? state.data.origin : window.location.origin
                    },
                    basket?.basketId,
                    basket?.customerInfo?.customerId
                )
                if (paymentsResponse?.isFinal && paymentsResponse?.isSuccessful) {
                    navigate(`/checkout/confirmation/${paymentsResponse?.merchantReference}`)
                }
            } catch (err) {
                navigate(`/checkout`)
            }
        },
        onSubmit: () => {},
        callbackIntents: ['SHIPPING_ADDRESS', 'SHIPPING_OPTION'],
        paymentDataCallbacks: {
            onPaymentDataChanged: (intermediatePaymentData) => {
                return new Promise((resolve) => {
                    const {callbackTrigger, shippingAddress, shippingOptionData} =
                        intermediatePaymentData
                    let paymentDataRequestUpdate = {}

                    const handlePaymentDataChanged = async () => {
                        if (
                            callbackTrigger === 'INITIALIZE' ||
                            callbackTrigger === 'SHIPPING_ADDRESS'
                        ) {
                            const phoneRegex = /^(?:\D*\d){10}\D*$/
                            if (!phoneRegex.test(shippingAddress.phoneNumber)) {
                                console.error('❌ SHIPPING_ADDRESS: invalid phone number')
                                // Return error if we can't get/create a basket
                                paymentDataRequestUpdate = {
                                    error: {
                                        reason: 'OTHER_ERROR',
                                        message: 'invalid phone number',
                                        intent: 'SHIPPING_ADDRESS'
                                    }
                                }
                                resolve(paymentDataRequestUpdate)
                                return
                            }
                            if (!basket || !basket?.basketId) {
                                console.error('❌ SHIPPING_ADDRESS: No basket available')
                                // Return error if we can't get/create a basket
                                paymentDataRequestUpdate = {
                                    error: {
                                        reason: 'OTHER_ERROR',
                                        message: 'Unable to process order',
                                        intent: 'SHIPPING_ADDRESS'
                                    }
                                }
                                resolve(paymentDataRequestUpdate)
                                return
                            }
                            const updateShippingAddressResponse = await updateShippingAddress(
                                authToken,
                                site,
                                basket,
                                shippingAddress,
                                fetchShippingMethods
                            )

                            paymentDataRequestUpdate =
                                updateShippingAddressResponse.paymentDataRequestUpdate
                        }
                        if (callbackTrigger === 'SHIPPING_OPTION') {
                            if (!basket || !basket.basketId) {
                                console.error('❌ SHIPPING_OPTION: No basket available')
                                // Return error if we can't get/create a basket
                                paymentDataRequestUpdate = {
                                    error: {
                                        reason: 'OTHER_ERROR',
                                        message: 'Unable to process order',
                                        intent: 'SHIPPING_OPTION'
                                    }
                                }
                                resolve(paymentDataRequestUpdate)
                                return
                            }

                            const updateShippingOptionResponse = await updateShippingOption(
                                authToken,
                                site,
                                basket,
                                shippingOptionData?.id
                            )

                            paymentDataRequestUpdate =
                                updateShippingOptionResponse.paymentDataRequestUpdate
                        }
                        resolve(paymentDataRequestUpdate)
                    }

                    handlePaymentDataChanged()
                })
            }
        }
    }
    return buttonConfig
}

const GooglePayExpressComponent = (props) => {
    const {
        adyenEnvironment,
        adyenPaymentMethods,
        basket,
        locale,
        site,
        authToken,
        navigate,
        fetchShippingMethods,
        isMiniCart
    } = useAdyenExpressCheckout()
    const paymentContainer = useRef(null)

    useEffect(() => {
        const createCheckout = async () => {
            try {
                const checkout = await AdyenCheckout({
                    environment: adyenEnvironment?.ADYEN_ENVIRONMENT,
                    clientKey: adyenEnvironment?.ADYEN_CLIENT_KEY,
                    locale: locale.id,
                    analytics: {
                        analyticsData: {
                            applicationInfo: adyenPaymentMethods?.applicationInfo
                        }
                    }
                })
                const googleButtonConfig = getGoogleButtonConfig(
                    authToken,
                    site,
                    basket,
                    adyenPaymentMethods,
                    navigate,
                    fetchShippingMethods
                )
                const googlePayButton = checkout.create(PAYMENT_METHOD, googleButtonConfig)
                const isGooglePayButtonAvailable = await googlePayButton.isAvailable()
                if (isGooglePayButtonAvailable && googleButtonConfig.configuration) {
                    googlePayButton.mount(paymentContainer.current)
                }
            } catch (err) {
                // Error
            }
        }
        if (adyenEnvironment && adyenPaymentMethods && basket && paymentContainer.current) {
            createCheckout()
        }
    }, [adyenEnvironment, adyenPaymentMethods])

    return (
        <>
            {props.showLoading && (
                <Flex align={'center'} justify={'center'}>
                    <Spinner size={'lg'} mt={4} />
                </Flex>
            )}
            <Flex
                justifyContent={'center'}
                sx={getGpayStyles(isMiniCart)}
                ref={paymentContainer}
            ></Flex>
        </>
    )
}

GooglePayExpressComponent.propTypes = {
    showLoading: PropTypes.bool,
    shippingMethods: PropTypes.array
}

export default GooglePayExpressComponent
